import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  setDoc,
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAt,
  startAfter,
  endAt,
  serverTimestamp,
  writeBatch,
  onSnapshot,
  runTransaction
} from 'firebase/firestore'
import { db } from '../firebase'
import config from '../config/environment'
import { normalizeLicensePlate } from '../utils/licensePlate'

// Development mode flag - now controlled by environment
const DEV_MODE = config.devMode

// No demo data is shipped. When DEV_MODE is enabled, prefer Firebase emulators.
const mockData = {
  washes: [],
  customers: [],
  subscriptions: [],
  inventory: [],
  expenses: [],
  dealers: [],
}

// Generic CRUD operations
export const firestoreService = {
  // Create document
  async create(collectionName, data) {
    const docRef = await addDoc(collection(db, collectionName), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  },

  // Get document by ID
  async getById(collectionName, id) {
    const docRef = doc(db, collectionName, id)
    const docSnap = await getDoc(docRef)
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null
  },

  // Update document
  async update(collectionName, id, data) {
    const docRef = doc(db, collectionName, id)
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    })
  },

  // Delete document
  async delete(collectionName, id) {
    const docRef = doc(db, collectionName, id)
    await deleteDoc(docRef)
  },

  // Get all documents in collection
  async getAll(collectionName, constraints = []) {
    const colRef = collection(db, collectionName)
    const q = constraints.length ? query(colRef, ...constraints) : colRef
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((docSnap) => {
      const data = docSnap.data() || {}
      const businessKey = data.id
      return {
        ...data,
        firestoreId: docSnap.id,
        // Always use Firestore doc id for updates; keep business numbers separately
        id: docSnap.id,
        ...(businessKey && businessKey !== docSnap.id
          ? {
              businessId: businessKey,
              invoiceNumber: data.invoiceNumber || businessKey,
            }
          : {}),
      }
    })
  },

  // Listen to real-time updates
  onSnapshot(collectionName, constraints = [], callback) {
    const q = query(collection(db, collectionName), ...constraints)
    return onSnapshot(q, (snapshot) => {
      const documents = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() || {}
        const businessKey = data.id
        return {
          ...data,
          firestoreId: docSnap.id,
          id: docSnap.id,
          ...(businessKey && businessKey !== docSnap.id
            ? {
                businessId: businessKey,
                invoiceNumber: data.invoiceNumber || businessKey,
              }
            : {}),
        }
      })
      callback(documents)
    })
  },

  // Batch operations
  batch() {
    return writeBatch(db)
  }
}

// Wash-specific operations
export const washService = {
  async createWash(washData) {
    if (DEV_MODE) {
      // Add to mock data and return ID
      const newWash = { ...washData, id: `wash-${Date.now()}`, createdAt: new Date() }
      mockData.washes.push(newWash)
      return newWash.id
    }
    
    const washId = await firestoreService.create('washes', {
      ...washData,
      createdAt: new Date()
    })

    // If the wash is paid via subscription, consume one wash from the active subscription
    if (washData?.paymentMethod === 'subscription' && washData?.customerId) {
      try {
        await subscriptionService.consumeWashForCustomer({
          customerId: washData.customerId,
          branchId: washData.branchId,
          washId,
        })
      } catch (e) {
        console.error('Failed to consume subscription wash:', e)
      }
    }

    return washId
  },

  async getWashesByBranch(branchId, status = null) {
    if (DEV_MODE) {
      // Handle "all branches" case in dev mode
      let washes = branchId === 'all-branches' 
        ? mockData.washes 
        : mockData.washes.filter(w => w.branchId === branchId)
      if (status) {
        washes = washes.filter(w => w.status === status)
      }
      return washes
    }
    
    // Handle "all branches" case in production
    if (branchId === 'all-branches') {
      const constraints = [orderBy('createdAt', 'desc')]
      if (status) {
        constraints.unshift(where('status', '==', status))
      }
      return await firestoreService.getAll('washes', constraints)
    }
    
    const constraints = [
      where('branchId', '==', branchId),
      orderBy('createdAt', 'desc')
    ]
    
    if (status) {
      constraints.splice(1, 0, where('status', '==', status))
    }

    return await firestoreService.getAll('washes', constraints)
  },

  async getCustomerWashes(customerId, branchId = null) {
    if (DEV_MODE) {
      // Return mock customer wash history
      if (customerId === 'customer-123') {
        return [
          {
            id: 'wash-1',
            customerId: 'customer-123',
            customerName: 'John Customer',
            serviceType: 'Premium Wash',
            amount: 150,
            status: 'completed',
            rating: 5,
            createdAt: new Date('2024-12-01'),
            carDetails: {
              make: 'Toyota',
              model: 'Camry',
              color: 'White',
              licensePlate: 'ABC123GP'
            }
          },
          {
            id: 'wash-2',
            customerId: 'customer-123',
            customerName: 'John Customer',
            serviceType: 'Standard Wash',
            amount: 80,
            status: 'completed',
            rating: 4,
            createdAt: new Date('2024-11-25'),
            carDetails: {
              make: 'Toyota',
              model: 'Camry',
              color: 'White',
              licensePlate: 'ABC123GP'
            }
          },
          {
            id: 'wash-3',
            customerId: 'customer-123',
            customerName: 'John Customer',
            serviceType: 'Premium Wash',
            amount: 150,
            status: 'completed',
            rating: 5,
            createdAt: new Date('2024-11-15'),
            carDetails: {
              make: 'Toyota',
              model: 'Camry',
              color: 'White',
              licensePlate: 'ABC123GP'
            }
          }
        ]
      }
      return []
    }

    const constraints = [where('customerId', '==', customerId)]
    if (branchId && branchId !== 'all-branches') {
      constraints.push(where('branchId', '==', branchId))
    }
    constraints.push(orderBy('createdAt', 'desc'))
    return await firestoreService.getAll('washes', constraints)
  },

  async getCustomerWashesByCustomerIds(customerIds = []) {
    if (DEV_MODE) return []
    const ids = Array.from(new Set((customerIds || []).filter(Boolean)))
    if (ids.length === 0) return []

    // Firestore 'in' supports up to 10 values. Chunk if needed.
    const chunks = []
    for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10))

    const all = []
    for (const chunk of chunks) {
      const list = await firestoreService.getAll('washes', [
        where('customerId', 'in', chunk),
        orderBy('createdAt', 'desc'),
      ])
      all.push(...list)
    }

    // Merge + de-dupe by id and sort newest first
    const map = new Map()
    for (const w of all) map.set(w.id, w)
    return Array.from(map.values()).sort((a, b) => {
      const da = a?.createdAt?.toDate?.() ? a.createdAt.toDate().getTime() : new Date(a?.createdAt || 0).getTime()
      const db = b?.createdAt?.toDate?.() ? b.createdAt.toDate().getTime() : new Date(b?.createdAt || 0).getTime()
      return db - da
    })
  },

  async getWashById(washId) {
    if (DEV_MODE) {
      return mockData.washes.find(w => w.id === washId)
    }
    
    return firestoreService.getById('washes', washId)
  },

  /** Most recent wash with a normalised plate (indexed lookup; fallback for legacy profiles). */
  async findRecentWashByPlate(normalizedPlate, branchId = null) {
    const norm = String(normalizedPlate || '').trim()
    if (!norm) return null

    if (DEV_MODE) {
      return (
        mockData.washes.find(
          (w) =>
            w.carDetails?.licensePlateNorm === norm ||
            normalizeLicensePlate(w.carDetails?.licensePlate) === norm
        ) || null
      )
    }

    const runQuery = async (constraints) => {
      try {
        const list = await firestoreService.getAll('washes', constraints)
        return list[0] || null
      } catch (e) {
        console.warn('Wash plate lookup query failed:', e)
        return null
      }
    }

    if (branchId && branchId !== 'all-branches') {
      const scoped = await runQuery([
        where('branchId', '==', branchId),
        where('carDetails.licensePlateNorm', '==', norm),
        orderBy('createdAt', 'desc'),
        limit(1),
      ])
      if (scoped) return scoped
    }

    return runQuery([
      where('carDetails.licensePlateNorm', '==', norm),
      orderBy('createdAt', 'desc'),
      limit(1),
    ])
  },

  async updateWashStatus(washId, status, additionalData = {}) {
    if (DEV_MODE) {
      console.log('Mock: Updating wash status', washId, status)
      return
    }

    if (status === 'cancelled') {
      const current = await firestoreService.getById('washes', washId)
      if (!current || current.status !== 'waiting') {
        throw new Error('Only waiting vehicles can be removed from the queue')
      }
    }
    
    const patch = {
      ...additionalData,
    }
    if (status) {
      patch.status = status
      patch[`${status}At`] = serverTimestamp()
    }
    await firestoreService.update('washes', washId, patch)
  },

  listenToWashQueue(branchId, callback) {
    const activeStatuses = ['waiting', 'washing', 'drying', 'polishing', 'interior', 'ready']

    if (DEV_MODE) {
      // Simulate real-time updates with mock data
      const mockWashes = branchId === 'all-branches'
        ? mockData.washes.filter((w) => activeStatuses.includes(w.status))
        : mockData.washes.filter(
            (w) => w.branchId === branchId && activeStatuses.includes(w.status)
          )
      setTimeout(() => callback(mockWashes), 100)
      return () => {} // Return cleanup function
    }

    if (branchId === 'all-branches') {
      return firestoreService.onSnapshot(
        'washes',
        [where('status', 'in', activeStatuses), orderBy('createdAt', 'asc')],
        callback
      )
    }

    return firestoreService.onSnapshot(
      'washes',
      [
        where('branchId', '==', branchId),
        where('status', 'in', activeStatuses),
        orderBy('createdAt', 'asc'),
      ],
      callback
    )
  }
}

// Customer-specific operations
export const customerService = {
  normalizePhone(input) {
    const raw = String(input || '').trim()
    if (!raw) return ''
    const digits = raw.replace(/[^\d+]/g, '').replace(/[^\d]/g, '')
    if (!digits) return ''
    // South Africa: accept +27 / 27 / 0
    if (digits.startsWith('27')) return `0${digits.slice(2)}`
    return digits
  },

  phoneCandidates(input) {
    const raw = String(input || '').trim()
    const norm = customerService.normalizePhone(raw)
    const digitsOnly = String(raw || '').replace(/\D/g, '')
    const set = new Set([raw, norm, digitsOnly].filter(Boolean))

    // If we have a local 0XXXXXXXXX, also add +27 variant.
    if (norm.startsWith('0') && norm.length >= 9) set.add(`+27${norm.slice(1)}`)
    // If we have 27XXXXXXXXX, add local 0 variant.
    if (digitsOnly.startsWith('27')) set.add(`0${digitsOnly.slice(2)}`)

    return Array.from(set).filter(Boolean).slice(0, 10)
  },

  makeCustomerCode(customerId) {
    const raw = String(customerId || '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
    const suffix = (raw.slice(-6) || '000000').padStart(6, '0')
    return `TS-${suffix}`
  },

  vehicleFromProfile(c) {
    const v = c?.primaryVehicle || c?.vehicle || c?.carDetails || {}
    return {
      make: String(v.make || v.brand || '').trim(),
      model: String(v.model || '').trim(),
      color: String(v.color || v.colour || '').trim(),
      licensePlate: String(v.licensePlate || v.registration || v.reg || '').trim(),
    }
  },

  async getCustomerByLicensePlate(plate) {
    const norm = normalizeLicensePlate(plate)
    if (!norm) return null
    if (DEV_MODE) {
      return (
        mockData.customers.find(
          (c) =>
            c.licensePlateNorm === norm ||
            normalizeLicensePlate(customerService.vehicleFromProfile(c).licensePlate) === norm
        ) || null
      )
    }
    const customers = await firestoreService.getAll('customers', [
      where('licensePlateNorm', '==', norm),
      limit(5),
    ])
    if (!customers.length) return null
    customers.sort(
      (a, b) => Number(b.totalWashes || 0) - Number(a.totalWashes || 0)
    )
    return customers[0]
  },

  async updatePrimaryVehicle(customerId, vehicle) {
    if (!customerId) return
    const licensePlate = String(vehicle?.licensePlate || '').trim()
    const norm = normalizeLicensePlate(licensePlate)
    const primaryVehicle = {
      make: String(vehicle?.make || '').trim() || null,
      model: String(vehicle?.model || '').trim() || null,
      color: String(vehicle?.color || '').trim() || null,
      licensePlate: licensePlate || null,
      type: vehicle?.type || null,
    }
    const patch = {
      primaryVehicle,
      updatedAt: serverTimestamp(),
    }
    if (norm) patch.licensePlateNorm = norm

    if (DEV_MODE) {
      const c = mockData.customers.find((x) => x.id === customerId)
      if (c) Object.assign(c, patch)
      return
    }

    await updateDoc(doc(db, 'customers', customerId), patch)
  },

  async createCustomer(customerData) {
    if (DEV_MODE) {
      console.log('Mock: Creating customer', customerData)
      return 'mock-customer-id'
    }
    const customerId = await firestoreService.create('customers', {
      ...customerData,
      customerCode: customerData?.customerCode || customerService.makeCustomerCode(`POS${Date.now()}`),
    })
    try {
      // Ensure every customer has a stable, unique customer code (shown on QR)
      const code = customerData?.customerCode || customerService.makeCustomerCode(customerId)
      await firestoreService.update('customers', customerId, {
        customerCode: code,
        updatedAt: serverTimestamp(),
      })
    } catch (e) {
      console.error('Failed to set customerCode:', e)
    }
    return customerId
  },

  async getCustomerByPhone(phone) {
    if (DEV_MODE) {
      return mockData.customers.find(c => c.phone === phone) || null
    }
    
    const candidates = customerService.phoneCandidates(phone)
    if (candidates.length === 0) return null

    const customers = await firestoreService.getAll('customers', [
      candidates.length === 1 ? where('phone', '==', candidates[0]) : where('phone', 'in', candidates),
    ])
    return customers[0] || null
  },

  async getCustomerByCode(code) {
    const c = String(code || '').trim().toUpperCase()
    if (!c) return null
    if (DEV_MODE) {
      return mockData.customers.find((x) => String(x.customerCode || '').toUpperCase() === c) || null
    }
    const customers = await firestoreService.getAll('customers', [where('customerCode', '==', c)])
    return customers[0] || null
  },

  async searchCustomerByNamePrefix(name) {
    const q = String(name || '').trim()
    if (!q) return null
    if (DEV_MODE) {
      const lower = q.toLowerCase()
      return mockData.customers.find((x) => String(x.name || '').toLowerCase().startsWith(lower)) || null
    }
    // Best-effort prefix match (Firestore is case-sensitive).
    const variants = Array.from(
      new Set([
        q,
        q.toLowerCase(),
        q.toUpperCase(),
        q.charAt(0).toUpperCase() + q.slice(1),
      ].filter(Boolean))
    )

    for (const v of variants) {
      const customers = await firestoreService.getAll('customers', [
        orderBy('name', 'asc'),
        startAt(v),
        endAt(v + '\uf8ff'),
        limit(1),
      ])
      if (customers?.[0]) return customers[0]
    }

    return null
  },

  async getCustomerById(customerId) {
    if (!customerId) return null
    if (DEV_MODE) return mockData.customers.find((c) => c.id === customerId) || null
    return await firestoreService.getById('customers', customerId)
  },

  async getCustomerByUserId(userId) {
    if (DEV_MODE) {
      if (userId === 'customer-123') {
        return {
          id: 'customer-123',
          userId: 'customer-123',
          name: 'John Customer',
          phone: '+27123456789',
          email: 'customer@example.com',
          loyaltyPoints: 150,
          totalWashes: 12,
          hasActiveSubscription: true,
          createdAt: new Date('2024-01-15'),
          lastVisit: new Date('2024-12-01')
        }
      }
      return null
    }

    const pickBestCandidate = (candidates) => {
      const scored = (candidates || []).map((c) => {
        const points = Number(c?.loyaltyPoints || 0)
        const washes = Number(c?.totalWashes || 0)
        const created =
          c?.createdAt?.toDate?.() ? c.createdAt.toDate().getTime() : new Date(c?.createdAt || 0).getTime()
        const updated =
          c?.updatedAt?.toDate?.() ? c.updatedAt.toDate().getTime() : new Date(c?.updatedAt || 0).getTime()
        // Prefer records with history/points; then prefer the most recently updated/created.
        const score = washes * 1000 + points * 10 + Math.max(updated || 0, created || 0) / 1e12
        return { c, score }
      })
      scored.sort((a, b) => b.score - a.score)
      return scored[0]?.c || null
    }

    // 1. Preferred: customer docs linked via userId field (there may be duplicates)
    const byUserId = await firestoreService.getAll('customers', [where('userId', '==', userId)])
    const bestLinked = pickBestCandidate(byUserId)
    if (bestLinked) return bestLinked

    // 2. Fallback: customers/{uid} (self-created profile)
    const selfRef = doc(db, 'customers', userId)
    const selfSnap = await getDoc(selfRef)
    if (selfSnap.exists()) return { id: selfSnap.id, ...selfSnap.data() }

    return null
  },

  async listCustomerIdsForUser(user) {
    if (!user?.uid) return []
    if (DEV_MODE) return [user.uid]

    const ids = new Set()

    const linked = await firestoreService.getAll('customers', [where('userId', '==', user.uid)])
    for (const c of linked) if (c?.id) ids.add(c.id)

    if (user.email) {
      const byEmail = await firestoreService.getAll('customers', [where('email', '==', user.email)])
      for (const c of byEmail) if (c?.id) ids.add(c.id)
    }

    // Always include self doc if it exists (even if empty)
    ids.add(user.uid)

    return Array.from(ids).filter(Boolean)
  },

  async getCustomersByIds(customerIds = []) {
    if (DEV_MODE) return []
    const ids = Array.from(new Set((customerIds || []).filter(Boolean)))
    if (ids.length === 0) return []

    const out = []
    // getDoc() is fine here (small set, no indexes required)
    for (const id of ids) {
      try {
        const snap = await getDoc(doc(db, 'customers', id))
        if (snap.exists()) out.push({ id: snap.id, ...snap.data() })
      } catch (e) {
        console.warn('Failed to load customer doc:', id, e)
      }
    }
    return out
  },

  async identityFromAuthUser(user) {
    let name = String(user?.displayName || '').trim()
    let phone = ''
    let email = String(user?.email || '').trim()
    try {
      if (user?.uid) {
        const userSnap = await getDoc(doc(db, 'users', user.uid))
        if (userSnap.exists()) {
          const u = userSnap.data() || {}
          name = name || String(u.name || u.displayName || '').trim()
          phone = String(u.phone || '').trim()
          email = email || String(u.email || '').trim()
        }
      }
    } catch (e) {
      console.warn('Could not read users profile for customer identity:', e)
    }
    return { name, phone, email }
  },

  async backfillCustomerIdentityFromUser(customerId, customerData, user) {
    if (!customerId) return
    const identity = await customerService.identityFromAuthUser(user)
    const patch = {}
    if (!String(customerData?.name || '').trim() && identity.name) patch.name = identity.name
    if (!String(customerData?.phone || '').trim() && identity.phone) patch.phone = identity.phone
    if (!String(customerData?.email || '').trim() && identity.email) patch.email = identity.email
    if (Object.keys(patch).length === 0) return
    patch.updatedAt = serverTimestamp()
    await updateDoc(doc(db, 'customers', customerId), patch)
  },

  /**
   * Find & link an existing staff-created customer record by email, then
   * fall back to creating a minimal shell at customers/{uid}.
   * Call this on every customer page load before getCustomerByUserId.
   */
  async ensureCustomerProfileForUser(user) {
    if (!user?.uid || DEV_MODE) return null

    // Already linked by userId?
    const existing = await firestoreService.getAll('customers', [where('userId', '==', user.uid)])

    // Check customers/{uid} (self-created shell/profile)
    const selfRef = doc(db, 'customers', user.uid)
    const selfSnap = await getDoc(selfRef)
    const selfData = selfSnap.exists() ? selfSnap.data() : null

    const pickBestCandidate = (candidates) => {
      const scored = (candidates || []).map((c) => {
        const points = Number(c?.loyaltyPoints || 0)
        const washes = Number(c?.totalWashes || 0)
        const created =
          c?.createdAt?.toDate?.() ? c.createdAt.toDate().getTime() : new Date(c?.createdAt || 0).getTime()
        const updated =
          c?.updatedAt?.toDate?.() ? c.updatedAt.toDate().getTime() : new Date(c?.updatedAt || 0).getTime()
        const score = washes * 1000 + points * 10 + Math.max(updated || 0, created || 0) / 1e12
        return { c, score }
      })
      scored.sort((a, b) => b.score - a.score)
      return scored[0]?.c || null
    }

    const bestExistingLinked = pickBestCandidate(existing)

    // Try to find a staff-created record that matches login email (or phone later)
    if (user.email) {
      const byEmail = await firestoreService.getAll('customers', [where('email', '==', user.email)])
      const unlinked = byEmail.filter((c) => !c.userId && c.id !== user.uid)
      const best = pickBestCandidate(unlinked)
      if (best) {
        const linkRef = doc(db, 'customers', best.id)
        await updateDoc(linkRef, {
          userId: user.uid,
          updatedAt: serverTimestamp(),
          customerCode: best.customerCode || customerService.makeCustomerCode(best.id),
        })

        // If a self shell exists, mark it as merged so admin list doesn't show duplicates.
        if (selfSnap.exists()) {
          await updateDoc(selfRef, {
            mergedInto: best.id,
            updatedAt: serverTimestamp(),
          })
        }

        console.log('[CustomerLink] Linked existing customer', best.id, 'to uid', user.uid)
        return best.id
      }
    }

    // If we already have linked docs, return the best one.
    if (bestExistingLinked?.id) {
      await customerService.backfillCustomerIdentityFromUser(
        bestExistingLinked.id,
        bestExistingLinked,
        user
      )
      return bestExistingLinked.id
    }

    // If self profile exists and is already owned by this uid, use it.
    if (selfSnap.exists() && selfData?.userId === user.uid) {
      await customerService.backfillCustomerIdentityFromUser(selfRef.id, selfData, user)
      return selfRef.id
    }

    // No existing record — create a shell, copying name/phone from the auth/users profile
    // so onboarding does not ask for client details a second time.
    const identity = await customerService.identityFromAuthUser(user)
    const minimal = {
      userId: user.uid,
      email: identity.email,
      name: identity.name,
      phone: identity.phone,
      isSystemProfile: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      customerCode: customerService.makeCustomerCode(user.uid),
    }
    await setDoc(selfRef, minimal, { merge: true })
    return selfRef.id
  },

  async getAllCustomers() {
    if (DEV_MODE) {
      return mockData.customers
    }

    // Do not orderBy('createdAt') in Firestore — docs missing that field are excluded.
    const all = await firestoreService.getAll('customers')
    return customerService.sortCustomersByCreatedAt(all)
  },

  sortCustomersByCreatedAt(list) {
    const toMs = (value) => {
      if (!value) return 0
      if (typeof value?.toDate === 'function') return value.toDate().getTime()
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? 0 : d.getTime()
    }
    return [...(list || [])].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))
  },

  async getCustomersByBranch(branchId) {
    if (DEV_MODE) {
      // In development, return all customers as they don't have branch associations in mock data
      return mockData.customers
    }

    if (!branchId || branchId === 'all-branches') {
      return await customerService.getAllCustomers()
    }

    const all = await customerService.getAllCustomers()
    // Include this branch + unassigned/legacy (no branchId). Self-registered
    // customers often have no branchId and were previously hidden.
    return all.filter((c) => {
      const bid = String(c?.branchId || '').trim()
      if (!bid || bid === 'all-branches') return true
      return bid === branchId
    })
  },

  async updateCustomer(customerId, updateData) {
    if (DEV_MODE) {
      console.log('Mock: Updating customer', customerId, updateData)
      return
    }
    
    await firestoreService.update('customers', customerId, updateData)
  },

  /** Add to running account balance (amount owed) for on-account customers */
  async adjustAccountBalance(customerId, amountDelta) {
    const delta = Number(amountDelta || 0)
    if (!customerId || !delta) return
    if (DEV_MODE) return

    const ref = doc(db, 'customers', customerId)
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref)
      if (!snap.exists()) return
      const prev = Number(snap.data().accountBalance || 0)
      tx.update(ref, {
        accountBalance: prev + delta,
        updatedAt: serverTimestamp(),
      })
    })
  },

  async mergeCustomers({ sourceCustomerId, targetCustomerId }) {
    if (!sourceCustomerId || !targetCustomerId) throw new Error('sourceCustomerId and targetCustomerId required')
    if (sourceCustomerId === targetCustomerId) return
    if (DEV_MODE) return

    const batch = writeBatch(db)

    // Mark source as merged
    batch.update(doc(db, 'customers', sourceCustomerId), {
      mergedInto: targetCustomerId,
      updatedAt: serverTimestamp(),
    })

    // If target exists, keep as-is; don't overwrite key fields here.

    // Move washes
    const washSnap = await getDocs(query(collection(db, 'washes'), where('customerId', '==', sourceCustomerId)))
    washSnap.docs.forEach((d) => batch.update(d.ref, { customerId: targetCustomerId, updatedAt: serverTimestamp() }))

    // Move vouchers
    const voucherSnap = await getDocs(query(collection(db, 'vouchers'), where('customerId', '==', sourceCustomerId)))
    voucherSnap.docs.forEach((d) => batch.update(d.ref, { customerId: targetCustomerId, updatedAt: serverTimestamp() }))

    // Move subscriptions
    const subSnap = await getDocs(query(collection(db, 'subscriptions'), where('customerId', '==', sourceCustomerId)))
    subSnap.docs.forEach((d) => batch.update(d.ref, { customerId: targetCustomerId, updatedAt: serverTimestamp() }))

    await batch.commit()
  },

  async ensureCustomerCode(customerId) {
    if (DEV_MODE) return customerService.makeCustomerCode(customerId)
    const ref = doc(db, 'customers', customerId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return null
    const existing = snap.data().customerCode
    if (existing) return existing
    const code = customerService.makeCustomerCode(customerId)
    await updateDoc(ref, { customerCode: code, updatedAt: serverTimestamp() })
    return code
  },

  /** Records the wash package + vehicle tier staff selected (shown on customer profile). */
  async appendWashSelection(customerId, entry) {
    const cap = 40
    const normalized = {
      ...entry,
      at: entry.at || new Date().toISOString(),
    }

    if (DEV_MODE) {
      const c = mockData.customers.find((x) => x.id === customerId)
      if (c) {
        const log = [...(c.washSelectionLog || []), normalized]
        c.washSelectionLog = log.slice(-cap)
        c.lastWashSelection = normalized
      }
      return
    }

    const ref = doc(db, 'customers', customerId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return

    const prev = snap.data().washSelectionLog || []
    const next = [...prev, normalized].slice(-cap)
    const car = entry.carDetails || {}
    const hasCar = ['make', 'model', 'color', 'licensePlate'].some((k) =>
      String(car[k] || '').trim()
    )
    const existingVehicle = snap.data().primaryVehicle || {}
    const missingProfileVehicle = !['make', 'model', 'color', 'licensePlate'].some((k) =>
      String(existingVehicle[k] || '').trim()
    )
    const patch = {
      washSelectionLog: next,
      lastWashSelection: normalized,
      updatedAt: serverTimestamp(),
    }
    if (hasCar && missingProfileVehicle) {
      const plateNorm = normalizeLicensePlate(car.licensePlate)
      patch.primaryVehicle = {
        make: String(car.make || '').trim() || null,
        model: String(car.model || '').trim() || null,
        color: String(car.color || '').trim() || null,
        licensePlate: String(car.licensePlate || '').trim() || null,
        type: existingVehicle.type || null,
      }
      if (plateNorm) patch.licensePlateNorm = plateNorm
    }
    await updateDoc(ref, patch)
  },

  async updateLoyaltyPoints(customerId, points) {
    if (DEV_MODE) {
      console.log('Mock: Updating loyalty points', customerId, points)
      return
    }
    
    const customer = await firestoreService.getById('customers', customerId)
    if (customer) {
      await firestoreService.update('customers', customerId, {
        loyaltyPoints: (customer.loyaltyPoints || 0) + points
      })
    }
  }
}

// Inventory operations
export const inventoryService = {
  async addInventoryItem(itemData) {
    if (DEV_MODE) {
      console.log('Mock: Adding inventory item', itemData)
      return 'mock-inventory-id'
    }
    return await firestoreService.create('inventory', itemData)
  },

  async getInventoryByBranch(branchId) {
    if (DEV_MODE) {
      return branchId === 'all-branches' 
        ? mockData.inventory 
        : mockData.inventory.filter(item => item.branchId === branchId)
    }
    
    if (branchId === 'all-branches') {
      return await firestoreService.getAll('inventory', [
        orderBy('name', 'asc')
      ])
    }
    
    return await firestoreService.getAll('inventory', [
      where('branchId', '==', branchId),
      orderBy('name', 'asc')
    ])
  },

  async updateStock(itemId, quantity, operation = 'subtract') {
    if (DEV_MODE) {
      console.log('Mock: Updating stock', itemId, quantity, operation)
      return
    }
    
    const item = await firestoreService.getById('inventory', itemId)
    if (item) {
      const newQuantity = operation === 'add' 
        ? item.currentStock + quantity 
        : item.currentStock - quantity
      
      await firestoreService.update('inventory', itemId, {
        currentStock: Math.max(0, newQuantity),
        lastUpdated: serverTimestamp()
      })
    }
  },

  async getLowStockItems(branchId, threshold = 10) {
    if (DEV_MODE) {
      return branchId === 'all-branches' 
        ? mockData.inventory.filter(item => item.currentStock <= threshold)
        : mockData.inventory.filter(item => item.branchId === branchId && item.currentStock <= threshold)
    }
    
    if (branchId === 'all-branches') {
      return await firestoreService.getAll('inventory', [
        where('currentStock', '<=', threshold)
      ])
    }
    
    return await firestoreService.getAll('inventory', [
      where('branchId', '==', branchId),
      where('currentStock', '<=', threshold)
    ])
  },

  async addStockMovement(movementData) {
    if (DEV_MODE) {
      console.log('Mock: Adding stock movement', movementData)
      return 'mock-movement-id'
    }
    
    return await firestoreService.create('stockMovements', {
      ...movementData,
      timestamp: serverTimestamp()
    })
  },

  async deleteItem(itemId) {
    if (DEV_MODE) {
      console.log('Mock: Deleting inventory item', itemId)
      return
    }
    await firestoreService.delete('inventory', itemId)
  }
}

// Subscription operations
export const subscriptionService = {
  async createSubscription(subscriptionData) {
    if (DEV_MODE) {
      console.log('Mock: Creating subscription', subscriptionData)
      return 'mock-subscription-id'
    }
    return await firestoreService.create('subscriptions', subscriptionData)
  },

  async getActiveSubscriptions(branchId) {
    if (DEV_MODE) {
      return branchId === 'all-branches' 
        ? mockData.subscriptions.filter(s => s.isActive)
        : mockData.subscriptions.filter(s => s.branchId === branchId && s.isActive)
    }
    
    if (branchId === 'all-branches') {
      return await firestoreService.getAll('subscriptions', [
        where('isActive', '==', true)
      ])
    }
    
    return await firestoreService.getAll('subscriptions', [
      where('branchId', '==', branchId),
      where('isActive', '==', true)
    ])
  },

  async getSubscriptionsByBranch(branchId) {
    if (DEV_MODE) {
      return branchId === 'all-branches' 
        ? mockData.subscriptions 
        : mockData.subscriptions.filter(s => s.branchId === branchId)
    }
    
    if (branchId === 'all-branches') {
      return await firestoreService.getAll('subscriptions', [
        orderBy('createdAt', 'desc')
      ])
    }
    
    return await firestoreService.getAll('subscriptions', [
      where('branchId', '==', branchId),
      orderBy('createdAt', 'desc')
    ])
  },

  async getCustomerSubscription(customerId) {
    if (DEV_MODE) {
      // Return mock subscription for customer
      if (customerId === 'customer-123') {
        return {
          id: 'subscription-123',
          customerId: 'customer-123',
          type: 'Premium Monthly',
          price: 500,
          washesRemaining: 8,
          totalWashes: 10,
          isActive: true,
          createdAt: new Date('2024-11-01'),
          expiresAt: new Date('2024-12-31'),
          features: ['Premium wash', 'Interior cleaning', 'Wax protection']
        }
      }
      return null
    }
    
    const subscriptions = await firestoreService.getAll('subscriptions', [
      where('customerId', '==', customerId),
      where('isActive', '==', true)
    ])
    return subscriptions[0] || null
  },

  async getCustomerSubscriptionByCustomerIds(customerIds = []) {
    if (DEV_MODE) return null
    const ids = Array.from(new Set((customerIds || []).filter(Boolean)))
    if (ids.length === 0) return null

    // Firestore 'in' supports up to 10 values.
    const chunks = []
    for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10))

    const all = []
    for (const chunk of chunks) {
      const subs = await firestoreService.getAll('subscriptions', [
        where('customerId', 'in', chunk),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        limit(10),
      ])
      all.push(...subs)
    }

    // Pick the newest active subscription
    all.sort((a, b) => {
      const da = a?.createdAt?.toDate?.() ? a.createdAt.toDate().getTime() : new Date(a?.createdAt || 0).getTime()
      const db = b?.createdAt?.toDate?.() ? b.createdAt.toDate().getTime() : new Date(b?.createdAt || 0).getTime()
      return db - da
    })
    return all[0] || null
  },

  async updateSubscription(subscriptionId, updateData) {
    if (DEV_MODE) {
      console.log('Mock: Updating subscription', subscriptionId, updateData)
      return
    }
    await firestoreService.update('subscriptions', subscriptionId, updateData)
  },

  async deleteSubscription(subscriptionId) {
    if (!subscriptionId) return
    if (DEV_MODE) return
    await firestoreService.delete('subscriptions', subscriptionId)
  },

  async deactivateSubscription(subscriptionId) {
    await firestoreService.update('subscriptions', subscriptionId, {
      isActive: false,
      deactivatedAt: serverTimestamp()
    })
  },

  async cancelSubscription(subscriptionId) {
    if (DEV_MODE) {
      console.log('Mock: Cancelling subscription', subscriptionId)
      return
    }
    await firestoreService.update('subscriptions', subscriptionId, {
      isActive: false,
      cancelledAt: serverTimestamp()
    })
  },

  async consumeWashForCustomer({ customerId, branchId, washId }) {
    if (!customerId) return

    // Find the customer's active subscription (prefer matching branch if provided)
    const constraints = [
      where('customerId', '==', customerId),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(5),
    ]
    const subs = await firestoreService.getAll('subscriptions', constraints)
    const now = new Date()

    const active =
      subs.find((s) => (branchId ? s.branchId === branchId : true) && (!s.expiryDate || new Date(s.expiryDate) > now)) ||
      subs.find((s) => !s.expiryDate || new Date(s.expiryDate) > now) ||
      subs[0]

    if (!active?.id) {
      throw new Error('No active subscription found for customer')
    }

    const subRef = doc(db, 'subscriptions', active.id)
    const custRef = doc(db, 'customers', customerId)

    await runTransaction(db, async (tx) => {
      const subSnap = await tx.get(subRef)
      if (!subSnap.exists()) throw new Error('Subscription not found')
      const sub = subSnap.data()

      const included = sub.washesIncluded
      const used = Number(sub.washesUsed || 0)
      const nextUsed = used + 1

      const updates = {
        washesUsed: nextUsed,
        lastUsedAt: serverTimestamp(),
        lastUsedWashId: washId || null,
        updatedAt: serverTimestamp(),
      }

      let shouldDeactivate = false
      if (included !== 'unlimited') {
        const total = Number(included || 0)
        if (total > 0 && nextUsed >= total) {
          shouldDeactivate = true
        }
      }

      if (sub.expiryDate) {
        const exp = new Date(sub.expiryDate)
        if (exp <= now) shouldDeactivate = true
      }

      if (shouldDeactivate) {
        updates.isActive = false
        updates.deactivatedAt = serverTimestamp()
      }

      tx.update(subRef, updates)

      if (shouldDeactivate) {
        tx.update(custRef, { hasActiveSubscription: false, updatedAt: serverTimestamp() })
      } else {
        tx.update(custRef, { hasActiveSubscription: true, updatedAt: serverTimestamp() })
      }
    })
  },
}

// Expense operations
export const expenseService = {
  async addExpense(expenseData) {
    if (DEV_MODE) {
      console.log('Mock: Adding expense', expenseData)
      return 'mock-expense-id'
    }
    return await firestoreService.create('expenses', expenseData)
  },

  async getExpensesByBranch(branchId, startDate, endDate) {
    if (DEV_MODE) {
      return branchId === 'all-branches' 
        ? mockData.expenses 
        : mockData.expenses.filter(e => e.branchId === branchId)
    }
    
    if (branchId === 'all-branches') {
      const constraints = [orderBy('date', 'desc')]
      
      if (startDate && endDate) {
        constraints.push(
          where('date', '>=', startDate),
          where('date', '<=', endDate)
        )
      }
      
      return await firestoreService.getAll('expenses', constraints)
    }
    
    const constraints = [
      where('branchId', '==', branchId),
      orderBy('date', 'desc')
    ]

    if (startDate && endDate) {
      constraints.push(
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      )
    }

    return await firestoreService.getAll('expenses', constraints)
  },

  async getExpensesByCategory(branchId, category, startDate, endDate) {
    const constraints = [
      where('branchId', '==', branchId),
      where('category', '==', category),
      orderBy('date', 'desc')
    ]
    
    if (startDate) {
      constraints.push(where('date', '>=', startDate))
    }
    if (endDate) {
      constraints.push(where('date', '<=', endDate))
    }

    return await firestoreService.getAll('expenses', constraints)
  },

  async deleteExpense(expenseId) {
    if (DEV_MODE) {
      console.log('Mock: Deleting expense', expenseId)
      return
    }
    await firestoreService.delete('expenses', expenseId)
  }
}

// Dealer operations
export const dealerService = {
  async createDealer(dealerData) {
    if (DEV_MODE) {
      console.log('Mock: Creating dealer', dealerData)
      return 'mock-dealer-id'
    }
    return await firestoreService.create('dealers', dealerData)
  },

  async getDealersByBranch(branchId) {
    if (DEV_MODE) {
      return branchId === 'all-branches' 
        ? mockData.dealers 
        : mockData.dealers.filter(dealer => dealer.branchId === branchId)
    }
    
    if (branchId === 'all-branches') {
      return await firestoreService.getAll('dealers', [
        orderBy('name', 'asc')
      ])
    }
    
    return await firestoreService.getAll('dealers', [
      where('branchId', '==', branchId),
      orderBy('name', 'asc')
    ])
  },

  async updateDealer(dealerId, updateData) {
    if (DEV_MODE) {
      console.log('Mock: Updating dealer', dealerId, updateData)
      return
    }
    await firestoreService.update('dealers', dealerId, updateData)
  },

  async deleteDealer(dealerId) {
    if (DEV_MODE) {
      console.log('Mock: Deleting dealer', dealerId)
      return
    }
    await firestoreService.delete('dealers', dealerId)
  },

  async addDealerWash(dealerId, washData) {
    const dealer = await firestoreService.getById('dealers', dealerId)
    if (dealer) {
      await firestoreService.update('dealers', dealerId, {
        totalWashes: (dealer.totalWashes || 0) + 1,
        totalAmount: (dealer.totalAmount || 0) + washData.amount,
        lastWashDate: serverTimestamp()
      })
    }
    
    return await firestoreService.create('dealerWashes', {
      ...washData,
      dealerId,
      timestamp: serverTimestamp()
    })
  }
} 

// Add POS service export
export { posService } from './posService'
export { roleService } from './roleService' 